import { useCallback, useEffect, useRef, useState } from "react";
import { toApiFailure } from "../../lib/errors";
import { deleteDraft, fetchDraft, putDraft, type DraftForm } from "./api";
import {
  bufferChange,
  draftBufferKey,
  dropBuffered,
  markSynced,
  readBuffered,
  type DraftPayload,
} from "./draft-buffer";
import { DraftSaver, pickRestoreCandidate, type DraftSaveStatus } from "./draft-saver";

/**
 * [NF-022] for one mask: autosave without the user asking, and the offer to
 * continue where they left off ([AC-030]).
 *
 * On mount the hook asks the server for the last autosaved state and this
 * page's volatile buffer for anything newer the server never confirmed; the
 * newer of the two is `offered`. Saves are held back until that answer is in,
 * so a first keystroke cannot overwrite what is about to be offered.
 *
 * The mask reports every change through `schedule` - its values, or null when
 * nothing differs from what it started with. After a deliberate save the mask
 * calls `clear`; the server also drops the draft in the save's own
 * transaction, `clear` covers a save request racing an autosave.
 */

/** How long saves wait for the server to say what is already stored. */
export const DRAFT_LOAD_HOLD_MS = 5_000;

export interface OfferedDraft {
  payload: DraftPayload;
  /** ISO time of the offered state. */
  at: string;
}

export interface InputDraft {
  /** The unsaved state found on mount, until the user restores or discards it. */
  offered: OfferedDraft | null;
  status: DraftSaveStatus;
  /** epoch ms of the last confirmed autosave in this mount. */
  savedAt: number | null;
  schedule: (payload: DraftPayload | null) => void;
  /** Take the offered state; the mask fills its fields with it. */
  restore: () => DraftPayload | null;
  /** Discard the offered state ([AC-030]). */
  dismiss: () => void;
  /** After a deliberate save: nothing queued is sent, the stored state goes. */
  clear: () => Promise<void>;
}

export function useInputDraft(args: {
  form: DraftForm;
  /** Empty for the Erfassungsmaske, the incident id for a Postmortem-Maske. */
  context: string;
  /** users.id of the signed-in person; null disables the hook. */
  userId: string | null | undefined;
  enabled?: boolean;
}): InputDraft {
  const { form, context, userId } = args;
  const enabled = (args.enabled ?? true) && Boolean(userId);
  const key = userId ? draftBufferKey(userId, form, context) : null;

  const [offered, setOffered] = useState<OfferedDraft | null>(null);
  const [status, setStatus] = useState<DraftSaveStatus>("idle");
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const saverRef = useRef<DraftSaver | null>(null);
  const offeredRef = useRef<OfferedDraft | null>(null);
  // Has the user changed the mask since the offer was made? Then the server
  // already holds (or is about to hold) the new input, not the offered state.
  const changedSinceOffer = useRef(false);

  const offer = useCallback((next: OfferedDraft | null) => {
    offeredRef.current = next;
    changedSinceOffer.current = false;
    setOffered(next);
  }, []);

  useEffect(() => {
    if (!enabled || !key) return;
    let savedSinceMount = false;
    const saver = new DraftSaver(
      {
        save: (payload, opts) => putDraft(form, context, payload, opts),
        remove: () => deleteDraft(form, context),
        statusOf: (error) => toApiFailure(error).status,
        onStatus: setStatus,
        onSaved: (payload, at) => {
          savedSinceMount = true;
          markSynced(key, payload);
          setSavedAt(at);
        },
        onRemoved: () => dropBuffered(key),
      },
      { held: true },
    );
    saverRef.current = saver;
    let cancelled = false;
    // A server that does not answer must not keep autosave off for good.
    const holdLimit = setTimeout(() => saver.release(), DRAFT_LOAD_HOLD_MS);

    void (async () => {
      let server = null;
      try {
        server = await fetchDraft(form, context);
      } catch {
        // Unreachable right now: the buffer may still hold something, and
        // the mask works without an offer either way.
      }
      if (cancelled) return;
      clearTimeout(holdLimit);
      // Answered after the hold ran out and the user's own input was already
      // saved over it: there is nothing older left to offer.
      if (savedSinceMount) {
        saver.release();
        return;
      }
      const candidate = pickRestoreCandidate(server, readBuffered(key));
      offer(candidate ? { payload: candidate.payload, at: candidate.at } : null);
      // Typed and erased again before the answer came: that must not remove
      // the state that is now on offer.
      if (candidate && saver.pendingKind === "remove") void saver.cancel();
      // A change the server never confirmed goes to the server now, so it
      // survives a reload from here on.
      if (candidate?.source === "buffer" && !saver.hasUnconfirmed) {
        saver.schedule(candidate.payload);
        saver.release();
        void saver.flush();
      } else {
        saver.release();
      }
    })();

    const onOnline = (): void => saver.retryNow();
    const onPageHide = (): void => void saver.flush({ keepalive: true });
    const onVisibility = (): void => {
      if (document.visibilityState === "hidden") void saver.flush({ keepalive: true });
    };
    window.addEventListener("online", onOnline);
    window.addEventListener("pagehide", onPageHide);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      clearTimeout(holdLimit);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("pagehide", onPageHide);
      document.removeEventListener("visibilitychange", onVisibility);
      // Leaving the mask inside the app: send what is queued now. If it does
      // not arrive, the buffer still holds it for the next mount.
      void saver.flush();
      saver.dispose();
      if (saverRef.current === saver) saverRef.current = null;
    };
  }, [enabled, key, form, context, offer]);

  const schedule = useCallback(
    (payload: DraftPayload | null) => {
      const saver = saverRef.current;
      if (!saver || !key) return;
      if (payload === null) {
        // Back to where the mask started. While an offer is open that must not
        // remove the offered state behind the user's back - only stop saving.
        if (offeredRef.current && !changedSinceOffer.current) {
          void saver.cancel();
          return;
        }
        dropBuffered(key);
        saver.schedule(null);
        return;
      }
      changedSinceOffer.current = true;
      bufferChange(key, payload, Date.now());
      saver.schedule(payload);
    },
    [key],
  );

  const restore = useCallback((): DraftPayload | null => {
    const current = offeredRef.current;
    offer(null);
    if (current) schedule(current.payload);
    return current?.payload ?? null;
  }, [offer, schedule]);

  const dismiss = useCallback(() => {
    const changed = changedSinceOffer.current;
    offer(null);
    const saver = saverRef.current;
    // Unless the user has typed since, the server still holds the offered
    // state, and discarding it is what [AC-030] asks for.
    if (saver && !changed) {
      if (key) dropBuffered(key);
      void saver.discard();
    }
  }, [key, offer]);

  const clear = useCallback(async () => {
    offer(null);
    if (key) dropBuffered(key);
    const saver = saverRef.current;
    if (saver) await saver.discard();
  }, [key, offer]);

  return { offered, status, savedAt, schedule, restore, dismiss, clear };
}
